import {
  ChatBubbleOutline,
  Groups,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from "@mui/icons-material";
import {
  Collapse,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { Fragment, useEffect } from "react";
import { theme } from "../../../../../../App";
import useUIBp from "../../../../../../Hooks/useUIBoilerplate";
import useURLQuery from "../../../../../../Hooks/useURLQuery";
import { UIactions } from "../../../../../../store/slices/UI";
import { workerActions } from "../../../../../../store/slices/workers";
import { getChannelMessagesId } from "../../../../../Teams/Actions/getChannelMessages";
import { currentTeamsUI } from "./TeamCategories";

export const forceOpen = "forceDrawer";

// Mirrors MailCategories/Category but for Teams. The team root acts as a
// folder and channels are leaves; navigation targets channel-level URLs.
const Category = ({ tree, idx, level, root }) => {
  const { dispatch, navigate, UI } = useUIBp();
  const { teamId, channelId } = useURLQuery();
  const hover = UI[forceOpen];
  const self = tree[idx];
  const expanded = UI[self?.id];

  const isRoot = idx === 0;
  const selected = isRoot
    ? false
    : teamId === root && channelId === self?.id;
  const accentColor = selected ? "select.accent" : "primary.light";

  useEffect(() => {
    if (!self?.id) return;
    dispatch(UIactions.add({ id: currentTeamsUI, key: self.id }));
    return () => {
      dispatch(UIactions.remove({ id: currentTeamsUI, key: self.id }));
    };
  }, [dispatch, self?.id]);

  if (!self) return null;

  const newTheme = createTheme(theme, {
    palette: {
      action: { hover: "rgba(255, 255, 255, 0.8)" },
      primary: { main: "#ddd", light: "rgb(200,199,230)" },
      secondary: { main: "#ddd" },
      select: { main: "#18365a", accent: "#f0f0f0" },
    },
  });

  const toggleFolder = () => {
    dispatch(UIactions.toggle(self.id));
  };

  const onClick = () => {
    if (isRoot) {
      // Clicking on the team itself just toggles its channel list.
      toggleFolder();
      return;
    }
    navigate(`/dashboard/teams?teamId=${root}&channelId=${self.id}`);
    dispatch(workerActions.clear(getChannelMessagesId));
  };

  let secondaryAction;
  if (self.childrenIdx?.length) {
    secondaryAction = (
      <ThemeProvider theme={newTheme}>
        <IconButton
          {...{
            onClick: toggleFolder,
            sx: {
              padding: 0.5,
              backgroundColor: "rgba(255,255,255,0.15)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
            },
          }}
        >
          {expanded ? (
            <KeyboardArrowDown
              {...{ sx: { fontSize: 16, color: accentColor } }}
            />
          ) : (
            <KeyboardArrowRight
              {...{ sx: { fontSize: 16, color: accentColor } }}
            />
          )}
        </IconButton>
      </ThemeProvider>
    );
  }

  const children = (self.childrenIdx ?? []).map((cidx) => (
    <Category key={cidx} {...{ tree, idx: cidx, level: level + 1, root }} />
  ));

  const Icon = isRoot ? Groups : ChatBubbleOutline;

  return (
    <Fragment>
      <ListItem {...{ secondaryAction, sx: { padding: 0, height: "6vh" } }}>
        <ListItemButton
          {...{
            onClick,
            sx: {
              paddingLeft: hover ? 2 + level * 2 : 2,
              height: "6vh",
            },
          }}
        >
          <ListItemIcon>
            <Icon {...{ sx: { color: accentColor } }} />
          </ListItemIcon>
          <ListItemText
            {...{
              primary: self.displayName,
              primaryTypographyProps: {
                sx: { fontSize: "0.8rem", color: accentColor },
                noWrap: true,
              },
            }}
          />
        </ListItemButton>
      </ListItem>
      <Collapse in={expanded} unmountOnExit>
        {children}
      </Collapse>
    </Fragment>
  );
};

export default Category;
